import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Newspaper } from "lucide-react";

interface DashboardNewsProps {
  labels: {
    news: string;
    newFeature: string;
    siteCanvas: string;
    siteCanvasDescription: string;
    trySiteCanvas: string;
  };
  variant?: "hero" | "compact";
}

export function DashboardNews({ labels, variant = "hero" }: DashboardNewsProps) {
  if (variant === "compact") {
    return (
      <Link
        to="/storefront/collections"
        className="flex items-center gap-3 px-4 py-3 bg-card rounded-xl border border-border hover:bg-accent transition-colors group"
      >
        <Newspaper className="w-5 h-5 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">
            {labels.siteCanvas}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {labels.siteCanvasDescription}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
      </Link>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.3 }}
    >
      <h2 className="text-sm font-semibold text-foreground mb-3">
        {labels.news}
      </h2>

      <Link
        to="/storefront/collections"
        className="block relative rounded-md overflow-hidden group shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.2),0_8px_24px_rgba(0,0,0,0.3)]"
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full aspect-[21/9] object-cover"
        >
          <source
            src="https://procreate-assets-cdn.procreate.com/_nuxt/home_16_9.LBYVcH2f.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center p-6 md:p-8">
          <div className="max-w-sm">
            <span className="inline-block px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/90 bg-white/20 backdrop-blur-sm rounded-full mb-3">
              {labels.newFeature}
            </span>
            <h3 className="text-2xl md:text-3xl font-medium text-white mb-2">
              {labels.siteCanvas}
            </h3>
            <p className="text-sm text-white/80 mb-3 line-clamp-2">
              {labels.siteCanvasDescription}
            </p>
            <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-full group-hover:bg-primary/90 transition-all shadow-lg shadow-primary/30">
              {labels.trySiteCanvas}
              <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
      </Link>
    </motion.div>
  );
}
